import NotesCMS from "@/components/NotesCMS";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Archive, RotateCcw, Filter, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const BOOKING_VIEWS = [
  { key: "active", label: "Active" },
  { key: "archived", label: "Archived" },
];

export default function Admin() {
  const [bookings, setBookings] = useState([]);
  const [bookingView, setBookingView] = useState("active");
  const [testPreview, setTestPreview] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityRequests, setCityRequests] = useState([]);
  const [rebelList, setRebelList] = useState([]);
  const [calStatus, setCalStatus] = useState({ configured: false, connected: false });
  const [tab, setTab] = useState("bookings");

  const loadBookings = async (view = bookingView) => {
    const archived = view === "archived";
    const { data } = await api.get(`/admin/bookings?archived=${archived}`);
    setBookings(data);
  };

  const load = async () => {
    const [c, v, w, ci, cs, cr, rl] = await Promise.all([
      api.get("/admin/contacts"), api.get("/admin/vouchers"),
      api.get("/admin/waitlist"), api.get("/cities"),
      api.get("/admin/calendar/status").catch(() => ({ data: { configured: false, connected: false }})),
      api.get("/admin/city-requests").catch(() => ({ data: [] })),
      api.get("/admin/rebel-list").catch(() => ({ data: [] })),
    ]);
    setContacts(c.data); setVouchers(v.data);
    setWaitlist(w.data); setCities(ci.data); setCalStatus(cs.data);
    setCityRequests(cr.data); setRebelList(rl.data);
    await loadBookings();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadBookings(bookingView); /* eslint-disable-next-line */ }, [bookingView]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/bookings/${id}/approval`, { action });
      toast.success(action === "approve" ? "Approved. Calendar event pushed if connected." : "Declined & refunded (if paid).");
      loadBookings();
    } catch { toast.error("Action failed."); }
  };

  const refund = async (id) => {
    if (!window.confirm("Issue a full refund on this booking?")) return;
    try {
      await api.post(`/admin/bookings/${id}/refund`);
      toast.success("Refund issued.");
      loadBookings();
    } catch (err) { toast.error(err?.response?.data?.detail || "Refund failed."); }
  };

  const archiveOne = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/bookings/${id}/archive`, { reason: "owner manual archive" });
      toast.success("Archived. The record is kept — payment, slot and notifications untouched.");
      loadBookings();
    } catch (err) { toast.error(err?.response?.data?.detail || "Archive failed."); }
    finally { setBusyId(null); }
  };

  const restoreOne = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/bookings/${id}/restore`);
      toast.success("Restored to Active view.");
      loadBookings();
    } catch (err) { toast.error(err?.response?.data?.detail || "Restore failed."); }
    finally { setBusyId(null); }
  };

  const openTestArchiveModal = async () => {
    try {
      const { data } = await api.get(`/admin/bookings/archive-test-preview`);
      setTestPreview(data);
      setShowTestModal(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load preview."); }
  };

  const confirmBulkArchive = async () => {
    try {
      const { data } = await api.post(`/admin/bookings/archive-test-bulk`, {
        reason: "owner bulk test fixture cleanup",
      });
      toast.success(`Archived ${data.archived} test fixture${data.archived === 1 ? "" : "s"}.`);
      setShowTestModal(false);
      setTestPreview(null);
      loadBookings();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk archive failed."); }
  };

  const updateCity = async (city_id, status) => {
    try {
      const { data } = await api.put(`/admin/cities/${city_id}`, { status });
      toast.success(status === "available" && data.notified_now > 0
        ? `City opened. Notified ${data.notified_now} VIPs — the rest go out in 12h.`
        : "City updated.");
      load();
    } catch { toast.error("Update failed."); }
  };

  const connectCal = async () => {
    try {
      const { data } = await api.get(`/admin/calendar/connect?origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Calendar not configured yet."); }
  };

  const activeCount = useMemo(() => bookings.length, [bookings]);

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
      <div className="eyebrow">Owner Console</div>
      <h1 className="mt-4 h-editorial text-5xl">Admin.</h1>

      <div className="mt-8 flex gap-3 flex-wrap">
        {["bookings", "contacts", "vouchers", "waitlist", "cities", "city-requests", "rebel-list", "notes", "calendar"].map(t => (
          <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
            className={`px-5 py-2 text-xs font-mono-eyebrow uppercase tracking-[0.24em] rounded-full border ${tab === t ? "bg-[#0052FF] border-[#0052FF] text-white" : "border-white/15 text-zinc-400"}`}>{t.replace(/-/g, " ")}</button>
        ))}
      </div>

      {tab === "notes" && <NotesCMS />}

      {tab === "bookings" && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-3.5 h-3.5 text-zinc-500" />
              {BOOKING_VIEWS.map(v => (
                <button
                  key={v.key}
                  data-testid={`admin-bookings-view-${v.key}`}
                  onClick={() => setBookingView(v.key)}
                  className={`px-4 py-1.5 text-[11px] font-mono-eyebrow uppercase tracking-[0.22em] rounded-full border ${bookingView === v.key ? "bg-white text-[#0a0a0c] border-white" : "border-white/15 text-zinc-400 hover:text-white"}`}
                >
                  {v.label}
                </button>
              ))}
              <span className="ml-2 text-[11px] text-zinc-500 font-mono-eyebrow uppercase tracking-[0.18em]">
                {activeCount} shown
              </span>
            </div>
            <button
              data-testid="admin-archive-test-cta"
              onClick={openTestArchiveModal}
              className="rc-btn rc-btn-outline-new text-xs"
              style={{ padding: "0.55rem 1rem" }}
            >
              <Archive className="w-3.5 h-3.5" /> Archive Test Bookings
            </button>
          </div>

          <div className="space-y-3">
            {bookings.length === 0 && (
              <div data-testid="admin-bookings-empty" className="text-zinc-500 text-sm">
                {bookingView === "archived" ? "No archived bookings." : "No active bookings."}
              </div>
            )}
            {bookings.map(b => (
              <div key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`} className="hairline p-6 flex flex-wrap items-center gap-6 justify-between">
                <div className="min-w-0">
                  <div className="font-serif-editorial text-2xl break-words">{b.user_name || b.user_email}</div>
                  <div className="text-xs text-zinc-500 mt-1 break-words">{b.city_name} · {new Date(b.slot_iso).toLocaleString()} · {b.package_name}</div>
                  <div className="text-xs text-zinc-500 mt-1 break-words">{b.user_email} · ${(b.amount/100).toLocaleString()} · {b.payment_status}</div>
                  {b.archived && (
                    <div className="text-[10px] font-mono-eyebrow uppercase tracking-[0.22em] text-amber-300 mt-2">
                      Archived {b.archived_at ? new Date(b.archived_at).toLocaleDateString() : ""}
                      {b.archived_by ? ` · by ${b.archived_by}` : ""}
                      {b.archive_reason ? ` · ${b.archive_reason}` : ""}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[10px] font-mono-eyebrow uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border
                    ${b.status === "confirmed" ? "text-[#00E699] border-[#00E699]/40" :
                      b.status === "pending_approval" ? "text-[#D4AF37] border-[#D4AF37]/40" :
                      b.status === "declined" ? "text-red-400 border-red-400/40" :
                      "text-zinc-400 border-zinc-600"}`}>{(b.status || "").replace(/_/g, " ")}</span>
                  {!b.archived && b.payment_status === "paid" && b.status === "pending_approval" && (
                    <>
                      <button data-testid={`approve-${b.booking_id}`} onClick={() => act(b.booking_id, "approve")} className="rc-btn-gold text-[10px] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approve</button>
                      <button data-testid={`decline-${b.booking_id}`} onClick={() => act(b.booking_id, "decline")} className="rc-btn-ghost text-[10px] inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Decline & Refund</button>
                    </>
                  )}
                  {!b.archived && b.payment_status === "paid" && b.status === "confirmed" && b.refund_status !== "refunded" && (
                    <button data-testid={`refund-${b.booking_id}`} onClick={() => refund(b.booking_id)} className="rc-btn-ghost text-[10px] inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Refund</button>
                  )}
                  {b.refund_status === "refunded" && <span className="text-[10px] font-mono-eyebrow uppercase tracking-[0.2em] text-red-400">Refunded</span>}
                  {bookingView === "active" ? (
                    <button
                      data-testid={`archive-${b.booking_id}`}
                      onClick={() => archiveOne(b.booking_id)}
                      disabled={busyId === b.booking_id}
                      className="rc-btn-ghost text-[10px] inline-flex items-center gap-1 disabled:opacity-50"
                      title="Hide from active view. Payment, slot, and notifications are not touched."
                    >
                      <Archive className="w-3 h-3" /> Archive
                    </button>
                  ) : (
                    <button
                      data-testid={`restore-${b.booking_id}`}
                      onClick={() => restoreOne(b.booking_id)}
                      disabled={busyId === b.booking_id}
                      className="rc-btn-ghost text-[10px] inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <RotateCcw className="w-3 h-3" /> Restore
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {showTestModal && testPreview && (
            <div
              data-testid="archive-test-modal"
              className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
              onMouseDown={(e) => { if (e.target === e.currentTarget) setShowTestModal(false); }}
            >
              <div
                role="dialog"
                aria-modal="true"
                className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0f0f12] overflow-hidden"
                style={{ maxHeight: "min(88vh, 720px)" }}
              >
                <div className="px-5 sm:px-6 pt-5 pb-3 border-b border-white/10 flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-300 mt-1" />
                  <div>
                    <div className="text-[11px] font-mono-eyebrow uppercase tracking-[0.22em] text-amber-300">Reversible archive</div>
                    <h3 className="font-display text-xl font-semibold mt-1">Archive {testPreview.count} test fixture{testPreview.count === 1 ? "" : "s"}?</h3>
                    <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">
                      Metadata only. Payments, Stripe refunds, slot reservations, calendar events, email/SMS — all untouched. Any item can be restored.
                      Owner emails ({testPreview.owner_protected_emails.join(", ")}) and non-{testPreview.test_domain} addresses are never included.
                    </p>
                  </div>
                </div>
                <div className="overflow-y-auto px-5 sm:px-6 py-4" style={{ maxHeight: "calc(min(88vh, 720px) - 170px)" }}>
                  {testPreview.count === 0 ? (
                    <div className="text-sm text-zinc-500 py-6 text-center">No test fixtures match the strict criteria right now.</div>
                  ) : (
                    <ul className="space-y-2 text-[12px] font-mono-eyebrow">
                      {testPreview.candidates.map(c => (
                        <li key={c.booking_id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-1.5">
                          <span className="truncate text-zinc-300">{c.user_email}</span>
                          <span className="text-zinc-500 shrink-0">{c.package_name} · ${((c.amount || 0) / 100).toLocaleString()} · {c.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="px-5 sm:px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2">
                  <button
                    data-testid="archive-test-cancel"
                    onClick={() => { setShowTestModal(false); setTestPreview(null); }}
                    className="rc-btn rc-btn-outline-new text-xs"
                    style={{ padding: "0.55rem 1rem" }}
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="archive-test-confirm"
                    disabled={testPreview.count === 0}
                    onClick={confirmBulkArchive}
                    className="rc-btn rc-btn-blue text-xs disabled:opacity-40"
                    style={{ padding: "0.55rem 1rem" }}
                  >
                    Archive {testPreview.count}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "contacts" && (
        <div className="mt-8 space-y-3">
          {contacts.length === 0 && <div className="text-zinc-500 text-sm">No inquiries yet.</div>}
          {contacts.map(c => (
            <div key={c.contact_id} className="hairline p-6">
              <div className="font-medium">{c.name} — <span className="text-zinc-500 text-sm">{c.email}</span></div>
              {c.city && <div className="text-xs text-zinc-500 mt-1">City: {c.city}</div>}
              <p className="mt-3 text-sm text-zinc-300 whitespace-pre-wrap">{c.message}</p>
              <div className="mt-2 text-[10px] text-zinc-500 font-mono-eyebrow uppercase tracking-[0.18em]">{new Date(c.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "vouchers" && (
        <div className="mt-8 space-y-3">
          {vouchers.length === 0 && <div className="text-zinc-500 text-sm">No vouchers yet.</div>}
          {vouchers.map(v => (
            <div key={v.code} className="hairline p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-mono-eyebrow tracking-[0.2em] text-[#D4AF37] text-lg">{v.code}</div>
                <div className="text-xs text-zinc-500 mt-1">Issued by {v.issued_by_email} · ${(v.amount_cents/100).toFixed(0)} off</div>
              </div>
              <span className={`text-[10px] font-mono-eyebrow uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${v.status === "active" ? "text-[#00E699] border-[#00E699]/40" : v.status === "redeemed" ? "text-[#00D1FF] border-[#00D1FF]/40" : "text-zinc-500 border-zinc-700"}`}>{v.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "waitlist" && (
        <div className="mt-8 space-y-3">
          {waitlist.length === 0 && <div className="text-zinc-500 text-sm">No waitlist entries yet.</div>}
          {waitlist.map(w => (
            <div key={w.waitlist_id} data-testid={`waitlist-${w.waitlist_id}`} className="hairline p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-medium">{w.name || w.email || w.phone}</div>
                <div className="text-xs text-zinc-500 mt-1">{w.city_name} · {w.contact_method?.toUpperCase()} · {w.email || w.phone}</div>
              </div>
              <span className={`text-[10px] font-mono-eyebrow uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${w.status === "notified" ? "text-[#00E699] border-[#00E699]/40" : w.status === "queued" ? "text-[#D4AF37] border-[#D4AF37]/40" : "text-zinc-400 border-zinc-600"}`}>{w.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "cities" && (
        <div className="mt-8 space-y-3">
          <p className="text-xs text-zinc-500 font-mono-eyebrow uppercase tracking-[0.24em]">Flip a city to <span className="text-[#00E699]">now_booking</span> to trigger the waitlist wave.</p>
          {cities.map(c => (
            <div key={c.city_id} data-testid={`city-row-${c.city_id}`} className="hairline p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-serif-editorial text-2xl">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{c.dates}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {["now_booking", "coming_soon", "heating_up", "requested", "closed"].map(s => (
                  <button key={s} data-testid={`city-${c.city_id}-set-${s}`} onClick={() => updateCity(c.city_id, s)}
                    className={`px-3 py-1.5 text-[10px] font-mono-eyebrow uppercase tracking-[0.24em] rounded-full border ${c.status === s ? "bg-[#D4AF37] border-[#D4AF37] text-[#050507]" : "border-white/15 text-zinc-400 hover:border-[#00D1FF]/50 hover:text-white"}`}>{s.replace(/_/g, " ")}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "city-requests" && (
        <div className="mt-8 space-y-3">
          {cityRequests.length === 0 && <div className="text-zinc-500 text-sm">No city requests yet.</div>}
          {cityRequests.map(r => (
            <div key={r.request_id} className="hairline p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-serif-editorial text-2xl">{r.city}</div>
                <div className="text-xs text-zinc-500 mt-1">{r.name || "—"} · {r.email || r.phone}</div>
              </div>
              <span className="text-[10px] font-mono-eyebrow uppercase tracking-[0.28em] text-[#D4AF37]">{r.notify_when_open ? "notify on open" : "no notify"}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "rebel-list" && (
        <div className="mt-8 space-y-3">
          {rebelList.length === 0 && <div className="text-zinc-500 text-sm">The Rebel List is empty.</div>}
          {rebelList.map(r => (
            <div key={r.email} className="hairline p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-medium">{r.name || r.email}</div>
                <div className="text-xs text-zinc-500 mt-1">{r.email}{r.cities_of_interest ? ` · ${r.cities_of_interest}` : ""}</div>
              </div>
              <span className="text-[10px] font-mono-eyebrow uppercase tracking-[0.28em] text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "calendar" && (
        <div className="mt-8 hairline p-8">
          <div className="eyebrow">Google Calendar Sync</div>
          <h2 className="mt-3 h-editorial text-3xl">Push approvals to your calendar.</h2>
          <p className="mt-4 text-sm text-zinc-400 max-w-xl">Every booking you approve gets a discreet event titled <span className="italic-serif text-[#D4AF37]">&quot;R&amp;Co Consulting&quot;</span> — no client PII in the calendar view. You authorize once; refresh tokens do the rest.</p>
          <div className="mt-6 space-y-2 text-sm">
            <div>Configured: <span className={calStatus.configured ? "text-[#00E699]" : "text-red-400"}>{calStatus.configured ? "yes" : "no — add GOOGLE_CAL_CLIENT_ID / GOOGLE_CAL_CLIENT_SECRET"}</span></div>
            <div>Connected: <span className={calStatus.connected ? "text-[#00E699]" : "text-zinc-500"}>{calStatus.connected ? "yes" : "not yet"}</span></div>
          </div>
          <button data-testid="calendar-connect" onClick={connectCal} disabled={!calStatus.configured} className="mt-6 rc-btn-gold disabled:opacity-40">{calStatus.connected ? "Reconnect Calendar" : "Connect Google Calendar"}</button>
        </div>
      )}
    </div>
  );
}
